/**
 * TtlGraph — a live, shrinking countdown of the session's remaining idle TTL.
 *
 * The full-width decay line runs from full (top-left) to empty (bottom-right)
 * over `ttlSeconds`; a playhead sweeps right with elapsed time and the filled
 * "remaining" wedge to its right shrinks toward zero. Each message (send OR
 * receive) resets `lastActivityAt`, so the wedge jumps back to full — the same
 * sliding idle timer the SessionContainer enforces, made visible. Colour shifts
 * green -> amber -> red as it depletes (reserved status colours, single series,
 * no legend needed).
 */
import { useEffect, useRef, useState } from 'react';

const OK = '#45d69f';
const WARN = '#f0b866';
const DANGER = '#ff6b6b';

function colourFor(fraction: number): string {
  if (fraction > 0.5) return OK;
  if (fraction > 0.2) return WARN;
  return DANGER;
}

export function TtlGraph(props: {
  ttlSeconds: number;
  lastActivityAt: number;
  active: boolean;
  destroyed: boolean;
  onExpire: () => void;
}): JSX.Element {
  const { ttlSeconds, lastActivityAt, active, destroyed, onExpire } = props;
  const [now, setNow] = useState(() => Date.now());
  const expired = useRef(false);

  useEffect(() => {
    expired.current = false;
  }, [lastActivityAt]);

  useEffect(() => {
    if (!active || destroyed) return;
    let raf = 0;
    const tick = (): void => {
      setNow(Date.now());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, destroyed, lastActivityAt]);

  const elapsedS = destroyed
    ? ttlSeconds
    : Math.min(ttlSeconds, Math.max(0, (now - lastActivityAt) / 1000));
  const remainingS = Math.max(0, ttlSeconds - elapsedS);
  const fraction = remainingS / ttlSeconds;

  useEffect(() => {
    if (!destroyed && active && remainingS <= 0 && !expired.current) {
      expired.current = true;
      onExpire();
    }
  }, [remainingS, destroyed, active, onExpire]);

  // Geometry.
  const W = 320;
  const H = 96;
  const px = W * (1 - fraction); // playhead x (elapsed)
  const pyLine = (x: number): number => H * (x / W); // decay line: full(top-left) -> empty(bottom-right)
  const colour = destroyed ? DANGER : colourFor(fraction);
  const remainingArea = `M ${px} ${pyLine(px)} L ${W} ${H} L ${px} ${H} Z`;
  const label = destroyed ? '0.0' : remainingS.toFixed(1);

  return (
    <div className="ttl-graph">
      <div className="ttl-head">
        <span className="ttl-label">Session TTL</span>
        <span className="ttl-count" style={{ color: colour }}>
          {label}s
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={`${label} seconds remaining`}>
        <defs>
          <linearGradient id="ttlFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colour} stopOpacity="0.42" />
            <stop offset="100%" stopColor={colour} stopOpacity="0.06" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1={0} y1={H * g} x2={W} y2={H * g} stroke="#232c3d" strokeWidth={1} />
        ))}
        {/* full decay reference line */}
        <line x1={0} y1={0} x2={W} y2={H} stroke="#2c384d" strokeWidth={1.5} strokeDasharray="3 4" />
        {/* shrinking remaining wedge */}
        <path d={remainingArea} fill="url(#ttlFill)" />
        <line x1={px} y1={pyLine(px)} x2={W} y2={H} stroke={colour} strokeWidth={2} strokeLinecap="round" />
        {!destroyed && <circle cx={px} cy={pyLine(px)} r={4.5} fill={colour} />}
      </svg>
    </div>
  );
}
